// Mise en forme permise à Lumi : paragraphes, listes « - », étapes numérotées « 1. », gras **…**, tableaux simples « | »
// et encadré « > ».
const LIST_LINE = /^[-•*]\s+/;
const CALLOUT_LINE = /^>\s*/;
const ORDERED_LINE = /^\d{1,2}[.)]\s+/;
const TABLE_SEPARATOR_CELL = /^:?-{2,}:?$/;
const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

export function parseEpiAnswer(text = "") {
  const lines = String(text).replace(/\r\n?/g, "\n").split("\n").map((line) => line.trim());
  const blocks = [];
  let paragraph = [];
  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ type: "paragraph", text: paragraph.join(" ") });
      paragraph = [];
    }
  };
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    if (!line) {
      flushParagraph();
    } else if (CALLOUT_LINE.test(line)) {
      flushParagraph();
      blocks.push({ type: "callout", tone: "result", text: line.replace(CALLOUT_LINE, "") });
    } else if (LIST_LINE.test(line) || ORDERED_LINE.test(line)) {
      flushParagraph();
      const pattern = LIST_LINE.test(line) ? LIST_LINE : ORDERED_LINE;
      const items = [];
      for (; index < lines.length && pattern.test(lines[index]); index += 1) {
        items.push(lines[index].replace(pattern, ""));
      }
      index -= 1;
      blocks.push(pattern === ORDERED_LINE ? { type: "list", items, ordered: true } : { type: "list", items });
    } else if (line.startsWith("|")) {
      flushParagraph();
      const rows = [];
      for (; index < lines.length && lines[index].startsWith("|"); index += 1) {
        const cells = lines[index].replace(/^\|/, "").replace(/\|$/, "").split("|").map((cell) => cell.trim());
        if (!cells.every((cell) => TABLE_SEPARATOR_CELL.test(cell))) {
          rows.push(cells);
        }
      }
      index -= 1;
      if (rows.length) {
        blocks.push({ type: "table", header: rows[0], rows: rows.slice(1) });
      }
    } else {
      paragraph.push(line);
    }
  }
  flushParagraph();
  return blocks;
}

// Une espace insécable retient « », ?, !, : et ; contre leur mot : aucun ne commence seul une ligne.
export const keepEpiPunctuation = (text = "") => String(text).replace(/« /g, "« ").replace(/ ([»?!:;])/g, " $1");

export function splitEpiBold(text = "") {
  return String(text).split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part) => (
    part.startsWith("**") && part.endsWith("**") && part.length > 4
      ? { bold: true, text: part.slice(2, -2) }
      : { bold: false, text: part }
  ));
}

const blockText = (block) => {
  if (block.type === "paragraph") return block.text;
  if (block.type === "list") return block.items.map((item, index) => `${block.ordered ? `${index + 1}.` : "-"} ${item}`).join("\n");
  if (block.type === "table") return [block.header, ...block.rows].map((row) => row.join(" | ")).join("\n");
  if (block.type === "callout") return block.title ? `${block.title} : ${block.text}` : block.text;
  return block.caption;
};

export const epiQuestionsText = (questions) => `Pour en savoir plus, demandez à Lumi : ${questions.map((question) => `« ${question} »`).join(" · ")}`;

// Le bouton Copier reprend le même contenu structuré que l'aperçu et les téléchargements.
export function epiDocumentText(document) {
  const cover = document.cover;
  return [
    document.title,
    ...(cover ? [cover.objective, cover.heading, cover.steps.map((step) => `${step.number}. ${step.title}${step.where ? ` (${step.where})` : ""}`).join("\n"),
      ...cover.boxes.map((box) => [box.title, box.note, ...box.items.map((item) => `- ${item}`)].filter(Boolean).join("\n")), ...cover.notes] : []),
    ...document.chapters.flatMap((chapter) => [chapter.kicker ? `${chapter.kicker} : ${chapter.title}` : chapter.title,
      ...chapter.blocks.map(blockText), ...(chapter.questions?.length ? [epiQuestionsText(chapter.questions)] : [])]),
    ...(document.glossary?.length ? ["Lexique", document.glossary.map((entry) => `${entry.term} : ${entry.definition}`).join("\n")] : []),
  ].filter(Boolean).join("\n\n");
}

const normalizeEpiText = (value) => String(value || "").replace(/\s+/g, " ").trim().toLocaleLowerCase("fr");

// Élément montré par Lumi, seulement sur sa page et s'il est affiché (pas dans un menu fermé, ni dans la fenêtre de Lumi).
export function findEpiTarget(show, root, pathname) {
  if (!show?.selector || pathname !== show.page.split("?")[0]) return null;
  const wanted = normalizeEpiText(show.text);
  return [...root.querySelectorAll(show.selector)].find((element) => (
    !element.closest("[data-epi-dialog]") && normalizeEpiText(element.textContent) === wanted && element.getClientRects().length > 0
  )) || null;
}

// Flèche du bord de la fenêtre de Lumi, tourné vers l'élément, jusqu'au côté de l'élément.
export function epiArrowPath(panel, target) {
  const toRight = target.left + target.width / 2 > panel.left + panel.width / 2;
  const startX = toRight ? panel.right + 6 : panel.left - 6;
  const endY = target.top + target.height / 2;
  const startY = Math.min(Math.max(endY, panel.top + 48), panel.bottom - 48);
  const endX = toRight ? target.left - 12 : target.right + 12;
  const middleX = (startX + endX) / 2;
  return `M ${startX} ${startY} C ${middleX} ${startY}, ${middleX} ${endY}, ${endX} ${endY}`;
}

// Contexte envoyé avec la question : la page, et la journée de dispatch ouverte s'il y en a une.
export function buildEpiContext(location, pageTitle = "") {
  const params = new URLSearchParams(location?.search || "");
  const rawDate = params.get("dispatch_date") || "";
  const dispatchDate = /^\d{4}-\d{2}-\d{2}$/.test(rawDate) ? rawDate : null;
  return {
    page: location?.pathname || "/",
    page_title: pageTitle || null,
    dispatch_date: dispatchDate,
    dispatch_period: dispatchDate ? (params.get("dispatch_period") === "evening" ? "evening" : "day") : null,
  };
}

export function describeEpiContext(context = {}) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(context?.dispatch_date || "");
  if (match) {
    const period = context.dispatch_period === "evening" ? "Soir" : "Jour";
    return { prefix: "Dispatch du", value: `${Number(match[3])} ${MONTHS[Number(match[2]) - 1]}`, suffix: `, ${period}` };
  }
  return context?.page_title ? { prefix: "Page", value: context.page_title, suffix: "" } : null;
}

// Consommation du mois, montrée à l'administrateur dans Captures > Robot : jetons lus et produits, part du plafond.
export function describeEpiUsage(usage) {
  if (!usage) return "";
  const amount = (value) => (value >= 1e6 ? `${(value / 1e6).toLocaleString("fr-CA", { maximumFractionDigits: 1 })} M`
    : value >= 1e3 ? `${Math.round(value / 1e3)} k` : String(value));
  const parts = [`Ce mois-ci : ${usage.questions} question${usage.questions > 1 ? "s" : ""}`];
  if (usage.measured) parts.push(`${amount(usage.input_tokens)} jetons lus`, `${amount(usage.output_tokens)} produits`);
  else if (usage.questions) parts.push("jetons non mesurés");
  if (usage.limit) parts.push(`${Math.round(((usage.input_tokens + usage.output_tokens) / usage.limit) * 100)} % du plafond de ${amount(usage.limit)} jetons`);
  return parts.join(" · ");
}
